import useEvent from '@lyearn/core/hooks/useEvent';
import { useFilterManager } from '@/components/FilterManager';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import useUpdateActionItem from '@/modules/tasks/features/actionItems/hooks/useUpdateActionItem';
import { useCreateActionItemsMutation } from '@/modules/tasks/graphql/queries/generated/createActionItems';
import { DashboardProps } from '@/modules/tasks/views/TasksDashboard/components/TaskDashboard/types';
import { RelatedEntityInput, RootActionItemFilter, TaskCreationTypeEnum } from '@/types/schema';

import { useCreateActionItemMutation } from '../graphql/mutations/generated/createActionItem';
import { useCreateActionItemAndUpdateMetricsMutation } from '../graphql/mutations/generated/createActionItemAndUpdateMetrics';
import { useDeleteActionItemMutation } from '../graphql/mutations/generated/deleteActionItem';
import { useDeleteActionItemAndUpdateMetricsMutation } from '../graphql/mutations/generated/deleteActionItemAndUpdateMetrics';
import { useUpdateActionItemAndUpdateMetricsMutation } from '../graphql/mutations/generated/updateActionItemAndUpdateMetrics';
import { ActionItemActions, ActionItemOnActionType } from '../types';

interface Props {
  entity?: RelatedEntityInput;
  onRefetchParentProgress?: DashboardProps['onRefetchParentProgress'];
  inViewFileId?: string;
  isSystemGeneratedFile?: boolean;
}

const useActionItemsAction = ({
  entity,
  onRefetchParentProgress,
  inViewFileId,
  isSystemGeneratedFile = false,
}: Props) => {
  const { state } = useFilterManager({});
  const viewValue = state.filter?.view;
  const rootActionItemStatus =
    viewValue == 'Separate' ? RootActionItemFilter.Both : RootActionItemFilter.RootActionItems;
  const [, deleteActionItem] = useDeleteActionItemMutation();
  const [, deleteActionItemAndUpdateMetrics] = useDeleteActionItemAndUpdateMetricsMutation();
  const { updateActionItem } = useUpdateActionItem();
  const [, updateActionItemAndUpdateMetrics] = useUpdateActionItemAndUpdateMetricsMutation();
  const [, createActionItem] = useCreateActionItemMutation();
  const [, createActionItemAndUpdateMetrics] = useCreateActionItemAndUpdateMetricsMutation();
  const [, createActionItems] = useCreateActionItemsMutation();
  const user = useUserContext();

  const onAction = useEvent(async ({ action, data }: ActionItemOnActionType) => {
    const {
      id,
      assignedToId,
      assignedToIds,
      dueDate,
      title,
      status,
      priority,
      fileId,
      statusId,
      category,
      order,
      stageIds,
    } = data;
    let response;

    switch (action) {
      case ActionItemActions.Delete:
        if (isSystemGeneratedFile && inViewFileId) {
          response = await deleteActionItemAndUpdateMetrics({
            data: { actionItemId: id },
            filters: { id: inViewFileId },
            widgetMetricsFilters: { rootActionItemStatus },
          });
        } else {
          response = await deleteActionItem({ data: { actionItemId: id } });
        }
        onRefetchParentProgress?.();

        return response?.data?.deleteActionItem?.success;
      case ActionItemActions.Add:
        const createActionItemInput = {
          title: title || '',
          assignedToId: user.id,
          entity,
          statusId,
          status,
          category,
          priority,
          fileId,
        };
        if (isSystemGeneratedFile && inViewFileId) {
          response = await createActionItemAndUpdateMetrics({
            data: createActionItemInput,
            filters: { id: inViewFileId },
            widgetMetricsFilters: { rootActionItemStatus },
          });
        } else {
          response = await createActionItem({
            data: createActionItemInput,
          });
        }
        onRefetchParentProgress?.();

        return response?.data?.createActionItem?.actionItem?.id;

      case ActionItemActions.ADD_MULTIPLE:
        response = await createActionItems({
          data: {
            actionItems: data.actionItems,
            taskCreationConfig: {
              taskCreationType: TaskCreationTypeEnum.Manual,
            },
          },
        });
        onRefetchParentProgress?.();

        return !!response?.data?.createActionItems.actionItems?.length;
      case ActionItemActions.Duplicate:
        response = await createActionItem({
          data: {
            title: title ?? '',
            assignedToId,
            entity,
            dueDate,
            statusId,
            status,
            category,
          },
        });
        onRefetchParentProgress?.();

        return response?.data?.createActionItem?.actionItem?.id;
      case ActionItemActions.Toggle:
        updateActionItem({
          data: {
            actionItemId: id,
            status: status ?? 'todo',
          },
        });
        break;
      case ActionItemActions.UpdateText:
        updateActionItem({
          data: {
            actionItemId: id,
            title: title ?? '',
          },
        });
        break;
      case ActionItemActions.UpdateDate:
        if (isSystemGeneratedFile && inViewFileId) {
          await updateActionItemAndUpdateMetrics({
            data: {
              actionItemId: id,
              dueDate: dueDate ?? undefined,
              toUnsetFields: dueDate ? undefined : { dueDate: true },
            },
            filters: { id: inViewFileId },
            widgetMetricsFilters: { rootActionItemStatus },
          });
        } else {
          await updateActionItem({
            data: {
              actionItemId: id,
              dueDate: dueDate ? dueDate : undefined,
              toUnsetFields: dueDate ? undefined : { dueDate: true },
            },
          });
        }

        onRefetchParentProgress?.();
        break;
      case ActionItemActions.UpdateAssignedTo:
        if (isSystemGeneratedFile && inViewFileId) {
          await updateActionItemAndUpdateMetrics({
            data: {
              actionItemId: id,
              assignedToId,
            },
            filters: { id: inViewFileId },
            widgetMetricsFilters: { rootActionItemStatus },
          });
        } else {
          updateActionItem({
            data: {
              actionItemId: id,
              assignedToId,
            },
          });
        }

        break;
      case ActionItemActions.UpdateAssignedToMultiple: {
        const userIds = (assignedToIds || []).filter(Boolean) as string[];
        if (isSystemGeneratedFile && inViewFileId) {
          await updateActionItemAndUpdateMetrics({
            data: {
              actionItemId: id,
              assignedToIds: userIds.length ? { set: userIds } : undefined,
              toUnsetFields: userIds.length ? undefined : { assignedToIds: true },
            },
            filters: { id: inViewFileId },
            widgetMetricsFilters: { rootActionItemStatus },
          });
        } else {
          updateActionItem({
            data: {
              actionItemId: id,
              assignedToIds: userIds.length ? { set: userIds } : undefined,
              toUnsetFields: userIds.length ? undefined : { assignedToIds: true },
            },
          });
        }

        break;
      }
      case ActionItemActions.UpdatePriority:
        await updateActionItem({
          data: {
            actionItemId: id,
            priority,
          },
        });
        onRefetchParentProgress?.();
        break;
      case ActionItemActions.UpdateFile:
        await updateActionItem({
          data: {
            actionItemId: id,
            fileId: fileId ? fileId : undefined,
            toUnsetFields: {
              fileId: fileId ? undefined : true,
              stageIds: true,
            },
          },
        });
        onRefetchParentProgress?.();
        break;
      case ActionItemActions.UpdateStatus:
        if (isSystemGeneratedFile && inViewFileId) {
          await updateActionItemAndUpdateMetrics({
            data: {
              actionItemId: id,
              statusId,
              status,
              category,
            },
            filters: { id: inViewFileId },
            widgetMetricsFilters: { rootActionItemStatus },
          });
        } else {
          await updateActionItem({
            data: {
              actionItemId: id,
              statusId,
              status,
              category,
            },
          });
        }

        onRefetchParentProgress?.();
        break;
      case ActionItemActions.UpdateOrder:
        await updateActionItem({
          data: {
            actionItemId: id,
            order,
          },
        });
        break;
      case ActionItemActions.UpdateStage:
        const updatedStageIds = (stageIds ?? []).filter(Boolean) as string[];
        await updateActionItem({
          data: {
            actionItemId: id,
            stageIds: updatedStageIds.length ? { set: updatedStageIds } : undefined,
            toUnsetFields: updatedStageIds.length ? undefined : { stageIds: true },
          },
        });
    }
  });

  return { onAction };
};
export default useActionItemsAction;
